import { Outlet, useLocation, Navigate, useNavigate } from 'react-router-dom'
import {
  Box,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material'
import DesktopNav from './DesktopNav'
import MobileNav from './MobileNav'
import PausedBanner from './PausedBanner'
import SubscriptionPitchBanner from './SubscriptionPitchBanner'
import Loading from './Loading'
import ProfileRegenBlockProvider from './ProfileRegenBlockProvider'
import { StagedContentGuardProvider } from './StagedContentGuardProvider'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'

const BLOCKED_PREFIXES = ['/funding-requests', '/applications', '/match', '/search']

function GuardedOutlet() {
  const { isOnboarding, isLoading: statusLoading } = useOnboardingStatus()
  const location = useLocation()
  const navigate = useNavigate()

  // Don't render content until we know onboarding status — prevents flash
  // of main app before redirecting onboarding users.
  if (statusLoading) return <Loading />
  if (!isOnboarding) return <Outlet />

  // User is mid-onboarding — check if they explicitly exited
  const hasExited = localStorage.getItem('onboarding_exited') === '1'

  // Allow invite routes through during onboarding
  if (!hasExited && !location.pathname.startsWith('/invite')) {
    return <Navigate to="/onboarding" replace />
  }

  // Landing page: send to profile (onboarding shell is a separate route)
  if (location.pathname === '/') {
    return <Navigate to="/profile/details" replace />
  }

  const isBlocked = BLOCKED_PREFIXES.some((p) =>
    location.pathname.startsWith(p)
  )
  if (!isBlocked) return <Outlet />

  // Blocked route during onboarding → dialog
  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>Complete Your Onboarding</DialogTitle>
      <DialogContent>
        <Typography sx={{ mt: 1 }}>
          Your organisation's onboarding isn't complete yet. Please finish
          setting up to access all features.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => navigate('/onboarding')}>
          Resume Onboarding
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function MainLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const NavComponent = isMobile ? MobileNav : DesktopNav

  return (
    <StagedContentGuardProvider>
      <ProfileRegenBlockProvider>
        <NavComponent />
        <PausedBanner />
        <SubscriptionPitchBanner />
        <Box sx={{ pt: 0, pb: 2 }} component="main">
          <GuardedOutlet />
        </Box>
      </ProfileRegenBlockProvider>
    </StagedContentGuardProvider>
  )
}
